// AdminActivityFeed.tsx — Phase 11G platform-wide activity timeline.
//
// Heterogeneous, chronologically sorted feed mixing:
//   - match settlements (per agent, per pillar)
//   - recharge requests
//   - partnership rate changes (cascade-aware)
//
// Same component reused with `scope='downline'` for the agent-side
// "My downline activity" page.
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// dense rows → spaced-out hover-highlight rows, fuchsia pills →
// admin's violet identity. Data + API calls + SSE wiring unchanged.

import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useSseAlerts } from '@baaligames/shared-ui/admin/lib/sseAlerts';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type WindowKey = '24h' | '7d' | '30d' | 'all';
type Kind = 'all' | 'match' | 'recharge' | 'partnership_change';

interface FeedItem {
  kind: 'match' | 'recharge' | 'partnership_change';
  at: string;
  agent_id: number | null;
  agent_username: string | null;
  match_id?: string;
  game_id?: string;
  pillar?: string;
  amount?: number;
  user_id?: number;
  state?: string;
  transaction_id?: string;
  old_rate_pct?: string | number;
  new_rate_pct?: string | number;
  is_cascade?: boolean;
  ref_id: number;
}

interface Resp {
  window: WindowKey; kind: Kind; items: FeedItem[];
  // Batch 6 #26: pagination metadata for Load More.
  limit?: number; offset?: number; hasMore?: boolean;
}

const PAGE_SIZE = 50;

const WINDOWS: Array<{ k: WindowKey; label: string }> = [
  { k: '24h', label: '24h' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'all', label: 'All' },
];
const KINDS: Array<{ k: Kind; label: string; icon: string }> = [
  { k: 'all',                 label: 'All',          icon: 'view_in_ar' },
  { k: 'match',               label: 'Matches',      icon: 'sports_esports' },
  { k: 'recharge',            label: 'Recharges',    icon: 'payments' },
  { k: 'partnership_change',  label: 'Rate changes', icon: 'history' },
];

const fmtSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN')}`;

export default function ActivityFeedPanel({
  scope = 'admin',
}: {
  scope?: 'admin' | 'downline';
} = {}) {
  const [windowK, setWindowK] = useState<WindowKey>('24h');
  const [kind, setKind] = useState<Kind>('all');
  const [items, setItems] = useState<FeedItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [livePulse, setLivePulse] = useState(0);  // increments on SSE arrival, drives Live indicator
  const [err, setErr] = useState<string | null>(null);

  const endpoint = scope === 'downline' ? '/admin/me-agent/activity-feed' : '/admin/activity-feed';
  // Reserved for an agent-scoped variant in a future iteration.
  // const isAdmin = scope === 'admin';

  // Initial load OR filter change → reset offset and fetch the first page.
  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<Resp>(`${endpoint}?window=${windowK}&kind=${kind}&limit=${PAGE_SIZE}&offset=0`);
      setItems(r.items || []);
      setHasMore(!!r.hasMore);
      setOffset(0);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, [windowK, kind, endpoint]);
  useEffect(() => { reload(); }, [reload]);

  // Phase 19 / Batch 6 #26: Load More — append the next page to items.
  // Uses the existing offset cursor so we don't re-shuffle items.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true); setErr(null);
    try {
      const next = offset + PAGE_SIZE;
      const r = await adminApi.get<Resp>(`${endpoint}?window=${windowK}&kind=${kind}&limit=${PAGE_SIZE}&offset=${next}`);
      // De-dupe by `${kind}-${ref_id}` in case SSE inserted overlapping items.
      const seen = new Set(items.map(i => `${i.kind}-${i.ref_id}`));
      const fresh = (r.items || []).filter(i => !seen.has(`${i.kind}-${i.ref_id}`));
      setItems((prev) => [...prev, ...fresh]);
      setHasMore(!!r.hasMore);
      setOffset(next);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoadingMore(false); }
  }, [endpoint, windowK, kind, offset, hasMore, items, loadingMore]);

  // Phase 19 / Batch 6 #26: live SSE prepend. We piggy-back on the
  // existing /api/admin/sse/alerts channel — its `match_finished` and
  // `rate_changed` event kinds map directly to two of our feed kinds.
  // When one arrives AND it matches the current filter, we fetch a
  // small head-page (limit=10, offset=0) and merge any new ref_ids
  // at the top. Recharge events don't have an SSE channel today so
  // they only show up on Refresh / Load More.
  const sse = useSseAlerts({
    endpoint: scope === 'downline' ? '/api/admin/me-agent/sse/alerts' : '/api/admin/sse/alerts',
    enabled: true,
    maxKeep: 10,
  });
  useEffect(() => {
    if (sse.alerts.length === 0) return;
    const newest = sse.alerts[0];
    // Only react to kinds that actually map to feed items.
    const isMatch = newest.kind === 'match_finished' && (kind === 'all' || kind === 'match');
    const isRate  = (newest.kind === 'rate_changed' || newest.kind === 'rate_clamped_cascade')
                    && (kind === 'all' || kind === 'partnership_change');
    if (!isMatch && !isRate) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<Resp>(`${endpoint}?window=${windowK}&kind=${kind}&limit=10&offset=0`);
        if (cancelled) return;
        const seen = new Set(items.map(i => `${i.kind}-${i.ref_id}`));
        const fresh = (r.items || []).filter(i => !seen.has(`${i.kind}-${i.ref_id}`));
        if (fresh.length > 0) {
          setItems((prev) => [...fresh, ...prev]);
          setLivePulse((n) => n + 1);
        }
      } catch { /* SSE-driven refresh; silently swallow blips */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sse.alerts, windowK, kind, endpoint]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}

        <motion.div
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                     backdrop-blur-md
                     bg-white/70 dark:bg-arena-900/60
                     flex flex-wrap gap-2 items-center"
        >
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Window</span>
          {WINDOWS.map(w => (
            <Pill key={w.k} active={windowK === w.k} onClick={() => setWindowK(w.k)}>{w.label}</Pill>
          ))}
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold ml-2">Kind</span>
          {KINDS.map(k => (
            <Pill key={k.k} active={kind === k.k} onClick={() => setKind(k.k)} icon={k.icon}>{k.label}</Pill>
          ))}
          <div className="flex-1" />
          {/* Batch 6 #26: Live indicator. Pulses on every SSE-driven
              prepend so the admin knows the feed is actually fresh. */}
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider transition ring-1 ring-inset ${
            livePulse > 0
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-300/60 dark:bg-emerald-400/20 dark:text-emerald-200 dark:ring-emerald-400/40'
              : 'bg-arena-100/60 text-arena-600 ring-arena-200/60 dark:bg-arena-700/50 dark:text-neon-200/60 dark:ring-arena-600/40'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${livePulse > 0 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)] animate-pulse' : 'bg-arena-400 dark:bg-neon-200/40'}`} />
            Live
          </span>
          <button onClick={reload}
                  className="grid place-items-center w-9 h-9 rounded-xl
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/80
                             hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="refresh" size={16} />
          </button>
        </motion.div>

        {loading && items.length === 0 && (
          <div className="text-center py-12 text-sm text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" /> Loading…
          </div>
        )}

        {!loading && items.length === 0 && (
          <EmptyState icon="dynamic_feed"
                      title="Quiet"
                      subtitle={`No ${kind === 'all' ? '' : kind + ' '}events in the ${windowK} window.`} />
        )}

        {items.length > 0 && (
          <div className="rounded-2xl
                          bg-white dark:bg-arena-700/40
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                          backdrop-blur
                          divide-y divide-arena-200/40 dark:divide-arena-700/40
                          overflow-hidden">
            {items.map((it, i) => (
              <motion.div key={`${it.kind}-${it.ref_id}`}
                          initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.005 * i }}
                          className="px-4 py-3.5 hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition flex items-start gap-3">
                <FeedIcon kind={it.kind} />
                <div className="min-w-0 flex-1 text-[12.5px]">
                  <FeedDescription it={it} />
                </div>
                <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50 whitespace-nowrap shrink-0">
                  {new Date(it.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Batch 6 #26: Load More — only when the backend told us there's
            another page. Cap is implicit: if the user keeps clicking,
            offset grows in PAGE_SIZE chunks until hasMore goes false. */}
        {items.length > 0 && (
          <div className="flex items-center justify-center pt-1">
            {hasMore ? (
              <button onClick={loadMore} disabled={loadingMore}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                 bg-violet-600 hover:bg-violet-500 text-white text-[12px] font-bold
                                 shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                                 transition active:scale-95 disabled:opacity-50">
                {loadingMore
                  ? <Icon name="progress_activity" size={13} className="animate-spin" />
                  : <Icon name="expand_more" size={13} />}
                {loadingMore ? 'Loading…' : `Load more (showing ${items.length})`}
              </button>
            ) : (
              <span className="text-[11px] text-arena-600 dark:text-neon-200/50">
                · End of feed · {items.length} event{items.length === 1 ? '' : 's'} ·
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FeedIcon({ kind }: { kind: FeedItem['kind'] }) {
  const map: Record<FeedItem['kind'], { icon: string; cls: string }> = {
    match:               { icon: 'sports_esports', cls: 'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30' },
    recharge:            { icon: 'payments',       cls: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30' },
    partnership_change:  { icon: 'percent',        cls: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30' },
  };
  const t = map[kind];
  return (
    <span className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ring-1 ${t.cls}`}>
      <Icon name={t.icon} size={18} filled />
    </span>
  );
}

function FeedDescription({ it }: { it: FeedItem }) {
  if (it.kind === 'match') {
    const amt = Number(it.amount || 0);
    const pillarLabel = it.pillar === 'platform_fee' ? 'platform fee' : it.pillar === 'win_loss' ? 'win/loss' : it.pillar === 'offer_reward' ? 'offer reward' : it.pillar;
    return (
      <>
        <span className="font-medium text-arena-900 dark:text-white">
          {it.agent_username
            ? <Link to={`/people/agents/${it.agent_id}`} className="font-bold hover:text-violet-600 dark:hover:text-violet-300">@{it.agent_username}</Link>
            : 'Settlement'}
        </span>{' '}
        <span className="text-arena-600 dark:text-neon-200/60">earned</span>{' '}
        <span className={`font-bold tabular-nums ${amt >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {fmtSigned(amt)}
        </span>{' '}
        <span className="text-arena-600 dark:text-neon-200/60">from {pillarLabel} on </span>
        <span className="capitalize text-arena-800 dark:text-neon-200/90">{it.game_id}</span>{' '}
        <span className="text-arena-500 dark:text-neon-200/40 font-mono text-[11px]">· {it.match_id}</span>
      </>
    );
  }
  if (it.kind === 'recharge') {
    const amt = Number(it.amount || 0);
    return (
      <>
        <span className="text-arena-600 dark:text-neon-200/60">Recharge</span>{' '}
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
          it.state === 'approved' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
          : it.state === 'rejected' ? 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
          : it.state === 'pending' ? 'bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'
          : 'bg-arena-100/60 text-arena-600 dark:bg-arena-700/50 dark:text-neon-200/60'
        }`}>{it.state}</span>{' '}
        <span className="text-arena-600 dark:text-neon-200/60">of</span>{' '}
        <span className="font-bold tabular-nums text-arena-900 dark:text-white">₹{amt.toLocaleString('en-IN')}</span>{' '}
        <span className="text-arena-600 dark:text-neon-200/60">by user #{it.user_id}</span>
        {it.agent_username && <>
          <span className="text-arena-600 dark:text-neon-200/60"> via </span>
          <Link to={`/people/agents/${it.agent_id}`} className="font-bold hover:text-violet-600 dark:hover:text-violet-300">@{it.agent_username}</Link>
        </>}
      </>
    );
  }
  // partnership_change
  const oldR = it.old_rate_pct == null ? null : Number(it.old_rate_pct);
  const newR = Number(it.new_rate_pct);
  const dir = oldR == null ? 0 : newR - oldR;
  return (
    <>
      <span className="text-arena-600 dark:text-neon-200/60">Rate change</span>{' '}
      <span className="font-medium text-arena-900 dark:text-white">
        {it.agent_username
          ? <Link to={`/people/agents/${it.agent_id}`} className="font-bold hover:text-violet-600 dark:hover:text-violet-300">@{it.agent_username}</Link>
          : ''}
      </span>{' '}
      <span className="text-arena-600 dark:text-neon-200/60">— {it.pillar} on </span>
      <span className="capitalize text-arena-800 dark:text-neon-200/90">{it.game_id}</span>{' '}
      <span className="font-mono text-[11.5px] text-arena-700 dark:text-neon-200/80">{oldR == null ? '—' : `${oldR}%`} → <b>{newR}%</b></span>{' '}
      {dir !== 0 && (
        <span className={`text-[10px] font-bold ${dir > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {dir > 0 ? '▲' : '▼'} {Math.abs(dir).toFixed(2)}
        </span>
      )}
      {it.is_cascade && (
        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9.5px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded
                         bg-amber-100 text-amber-700 ring-1 ring-amber-200
                         dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
          <Icon name="account_tree" size={10} /> cascade
        </span>
      )}
    </>
  );
}

function Pill({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon?: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
              active
                ? 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
            }`}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                    shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                    px-6 py-12 text-center backdrop-blur">
      <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                      bg-arena-100/60 dark:bg-arena-800/60 mb-3">
        <Icon name={icon} size={26} className="text-arena-400 dark:text-neon-200/40" />
      </div>
      <div className="font-bold text-arena-900 dark:text-white">{title}</div>
      <div className="text-[12px] text-arena-600 dark:text-neon-200/60 mt-1">{subtitle}</div>
    </div>
  );
}
